import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectIsSequenceMapPeekOpen,
  selectSequenceCanvasSelection,
  selectSequenceMapPeekNodeId,
  selectSequencePositionPicker,
} from "../../app/slices/GlobalSlice";
import {
  selectSelectedElement,
  selectSelectedElementType,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import {
  getSequenceStateAfterNode,
  parseActorPlace,
  parseHeldProp,
} from "../../helpers/SequenceStateAtNode";
import Interaction, { InteractionType } from "../../models/interactions/Interaction";
import { ISendActorToPosition } from "../../models/interactions/SendActorToPosition";
import { ISetCamera } from "../../models/interactions/SetCamera";
import { ISequence } from "../../models/interactions/SequenceModel";
import { getSequenceNodeTitle } from "../sequence/SequenceNodeTitle";

/**
 * The state the map shows below the sequence: where the actors and the camera
 * are once the node in focus has run (being picked for, selected, or last
 * picked for). Undefined when the map isn't open below a sequence or no single
 * node is in focus.
 */
export default function useSequenceStateOnMap() {
  const picker = useSelector(selectSequencePositionPicker);
  const isPeekOpen = useSelector(selectIsSequenceMapPeekOpen);
  const selection = useSelector(selectSequenceCanvasSelection);
  const selectedElement = useSelector(selectSelectedElement);
  const selectedElementType = useSelector(selectSelectedElementType);
  const take = useSelector(selectSelectedTake3D);

  const isSplit =
    (picker?.isOpen && picker.positionType !== "takeLocation") ||
    (!picker?.isOpen && isPeekOpen);
  const sequence =
    selectedElementType === "takeSequence" ||
    (selectedElementType === "interaction" &&
      (selectedElement as ISequence | undefined)?.type === InteractionType.Sequence)
      ? (selectedElement as ISequence | undefined)
      : undefined;
  const peekNodeId = useSelector(selectSequenceMapPeekNodeId);
  // The node being picked for, else the one selected, else the one last picked for.
  const nodeId = picker?.isOpen
    ? picker.nodeId
    : selection?.singleNodeId ?? peekNodeId ?? undefined;

  return useMemo(() => {
    if (!isSplit || !sequence || !take || !nodeId) return undefined;
    const node = sequence.nodes?.find((candidate) => candidate.id === nodeId);
    // No node, or On Take Start: nothing has happened yet, so the map shows the
    // take itself, editable.
    if (!node || isOnTakeStart(node.data.interaction)) return undefined;
    return {
      take,
      nodeTitle: getSequenceNodeTitle(node.data.interaction),
      // The position or camera the node itself targets: the map rims it.
      targetId: getNodeTargetId(node.data.interaction),
      state: getSequenceStateAfterNode(take, sequence, nodeId),
    };
  }, [isSplit, nodeId, sequence, take]);
}

/** Who stands on a position in the sequence state shown on the map. */
export interface SequenceActorPlacement {
  actorId: string;
  /** Put there by a node or interaction, rather than the take. */
  changed: boolean;
  /**
   * One of several places parallel branches leave the actor in no fixed order.
   * Drawn with a dashed border, without the dot.
   */
  uncertain: boolean;
  /** Holds a prop: the attachment marker draws the actor (and its dot) on top. */
  holdsProp: boolean;
}

/**
 * Position → actor in the sequence state on the map, keyed by the stored
 * position id and its last segment (layers use bare ids within a situation).
 * Actors on a prop (Player to Prop) stand on no position: the prop's attachment marker
 * draws them. Undefined when the map shows the take as it is.
 */
export function useSequenceActorPlacements() {
  const focus = useSequenceStateOnMap();
  return useMemo(() => {
    if (!focus) return undefined;
    const holders = new Set(
      Object.values(focus.state.heldProps).flatMap((value) =>
        value.values
          .map(parseHeldProp)
          .flatMap((held) => (held.kind === "heldBy" ? [held.actorId] : [])),
      ),
    );
    const placements = new Map<string, SequenceActorPlacement>();
    const place = (actorId: string, placeValue: string, placement: SequenceActorPlacement) => {
      const parsed = parseActorPlace(placeValue);
      if (parsed.kind !== "position") return;
      [parsed.positionId, parsed.positionId.split("/").pop()!].forEach((key) => {
        const existing = placements.get(key);
        // Two actors on one spot: a certain one first, then the one the sequence sent there.
        const wins =
          !existing ||
          (existing.uncertain && !placement.uncertain) ||
          (existing.uncertain === placement.uncertain && placement.changed && !existing.changed);
        if (wins) placements.set(key, placement);
      });
    };
    Object.entries(focus.state.actors).forEach(([actorId, value]) => {
      value.values.forEach((placeValue) =>
        place(actorId, placeValue, {
          actorId,
          changed: value.changed,
          uncertain: value.values.length > 1,
          holdsProp: holders.has(actorId),
        }),
      );
    });
    return placements;
  }, [focus]);
}

/**
 * Props the sequence has taken off their placement: in a hand (Prop to
 * Player) or destroyed by a Detach. Their spot shows as free. Undefined when
 * the map shows the take as it is.
 */
export function useSequenceRemovedPropIds() {
  const focus = useSequenceStateOnMap();
  return useMemo(() => {
    if (!focus) return undefined;
    return new Set(Object.keys(focus.state.heldProps));
  }, [focus]);
}

function getNodeTargetId(interaction: Interaction): string | undefined {
  if (interaction.type === InteractionType.SendActorToPosition)
    return (interaction as ISendActorToPosition).positionId || undefined;
  if (interaction.type === InteractionType.SetCamera)
    return (interaction as ISetCamera).cameraId || undefined;
  return undefined;
}

/**
 * Placed props an attachment marker draws on top of: an actor is on it (Player to Prop)
 * or got off beside it. Their own marker hides but stays clickable.
 */
export function useSequenceDockedPropIds() {
  const focus = useSequenceStateOnMap();
  return useMemo(() => {
    if (!focus) return undefined;
    return new Set(
      Object.values(focus.state.actors).flatMap((value) =>
        value.values
          .map(parseActorPlace)
          .flatMap((place) => (place.kind === "position" ? [] : [place.propId])),
      ),
    );
  }, [focus]);
}

const isOnTakeStart = (interaction: Interaction) =>
  interaction.type === InteractionType.Trigger &&
  (interaction as Interaction & { triggerType?: string }).triggerType === "onStart";
